import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ReplyIcon, TrashIcon } from '@heroicons/react/outline'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'

const SocialReplies = ({ postId, onReplyCountChange, initialReplies = [], onReplyCreated }) => {
  const [replies, setReplies] = useState(initialReplies)
  const [newReply, setNewReply] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loading, setLoading] = useState(initialReplies.length === 0)
  const [currentUser] = useGlobalState('currentUser')
  const { getRequest, putpostRequest, deleteRequest } = useQuery()

  useEffect(() => {
    if (initialReplies.length > 0) {
      setReplies(initialReplies)
      setLoading(false)
    } else {
      console.log('No initial replies, fetching from API...')
      if (replies.length === 0) {
        fetchReplies()
      }
    }
  }, [postId, initialReplies])

  const fetchReplies = () => {
    setLoading(true)
    getRequest(`/api/v3/social_posts/${postId}/social_replies`, {}, (err, response) => {
      setLoading(false)
      if (err) return
      setReplies(response || [])
    })
  }

  const handleSubmitReply = (e, parentReplyId = null) => {
    e.preventDefault()
    if (!newReply.trim() || isSubmitting) return

    setIsSubmitting(true)
    
    putpostRequest(`/api/v3/social_posts/${postId}/social_replies`, 'POST', {
      social_reply: { 
        content: newReply.trim(),
        parent_reply_id: parentReplyId
      }
    }, (err, response) => {
      setIsSubmitting(false)
      if (err) return
      
      setNewReply('')
      
      // If we have onReplyCreated, call it with the new reply
      if (onReplyCreated && response) {
        onReplyCreated(response)
      } else {
        // Otherwise fetch replies normally
        console.log('No onReplyCreated callback, fetching replies...')
        fetchReplies()
      }
      
      if (onReplyCountChange) onReplyCountChange(replies.length + 1)
    })
  }

  const handleDeleteReply = (replyId) => {
    if (!confirm('Are you sure you want to delete this reply?')) return
    
    deleteRequest(`/api/v3/social_posts/${postId}/social_replies/${replyId}`, {}, (err, response) => {
      if (err) return
      fetchReplies()
      if (onReplyCountChange) onReplyCountChange(replies.length - 1)
    })
  }

  const timeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) return `${diffInSeconds}s`
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`
    return `${Math.floor(diffInSeconds / 86400)}d`
  }

  const ReplyItem = ({ reply, isChild = false }) => {
    const [showReplyForm, setShowReplyForm] = useState(false)
    const [childReply, setChildReply] = useState('')

    const handleChildReply = (e) => {
      e.preventDefault()
      if (!childReply.trim()) return

      putpostRequest(`/api/v3/social_posts/${postId}/social_replies`, 'POST', {
        social_reply: { 
          content: childReply.trim(),
          parent_reply_id: reply.id
        }
      }, (err, response) => {
        if (err) return
        setChildReply('')
        setShowReplyForm(false)
        fetchReplies()
      })
    }

    return (
      <div className={`${isChild ? 'ml-8 mt-2' : 'mt-3'}`}>
        <div className="flex space-x-3">
          <img
            className="h-8 w-8 rounded-full"
            src={reply.user.image_url}
            alt={reply.user.display_name}
          />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-sm text-gray-900 dark:text-white">
                  {reply.user.display_name}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  @{reply.user.username}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  {timeAgo(reply.created_at)}
                </span>
              </div>
              {currentUser?.id === reply.user.id && (
                <button
                  onClick={() => handleDeleteReply(reply.id)}
                  className="text-gray-400 hover:text-red-500 p-1 rounded"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
              {reply.content}
            </p>
            {!isChild && (
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="mt-2 text-xs text-gray-500 hover:text-cccblue flex items-center space-x-1"
              >
                <ReplyIcon className="h-3 w-3" />
                <span>Reply</span>
              </button>
            )}
            
            {showReplyForm && (
              <form onSubmit={handleChildReply} className="mt-2">
                <textarea
                  rows={2}
                  value={childReply}
                  onChange={(e) => setChildReply(e.target.value)}
                  className="block w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:ring-cccblue focus:border-cccblue dark:bg-gray-800 dark:text-white resize-none"
                  placeholder="Write a reply..."
                  maxLength={500}
                />
                <div className="mt-2 flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowReplyForm(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!childReply.trim()}
                    className="text-sm px-3 py-1 bg-cccblue text-white rounded-md hover:bg-cccblue-alt disabled:opacity-50"
                  >
                    Reply
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
        
        {/* Render child replies */}
        {reply.child_replies && reply.child_replies.map(childReply => (
          <ReplyItem key={childReply.id} reply={childReply} isChild={true} />
        ))}
      </div>
    )
  }

  if (loading) return <Loading />

  return (
    <div className="border-l-2 border-gray-200 dark:border-gray-600 pl-4">
      {/* New reply form */}
      <form onSubmit={handleSubmitReply} className="mb-4">
        <div className="flex space-x-3">
          <img
            className="h-8 w-8 rounded-full"
            src={currentUser?.avatarUrl}
            alt={currentUser?.displayName}
          />
          <div className="flex-1">
            <textarea
              rows={2}
              value={newReply}
              onChange={(e) => setNewReply(e.target.value)}
              className="block w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:ring-cccblue focus:border-cccblue dark:bg-gray-800 dark:text-white resize-none"
              placeholder="Write a reply..."
              maxLength={500}
              disabled={isSubmitting}
            />
            <div className="mt-2 flex justify-end">
              <button
                type="submit"
                disabled={!newReply.trim() || isSubmitting}
                className="text-sm px-3 py-1 bg-cccblue text-white rounded-md hover:bg-cccblue-alt disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Replying...' : 'Reply'}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Replies list */}
      <div className="space-y-2">
        {replies.map(reply => (
          <ReplyItem key={reply.id} reply={reply} />
        ))}
        {replies.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">No replies yet.</p>
        )}
      </div>
    </div>
  )
}

SocialReplies.propTypes = {
  postId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onReplyCountChange: PropTypes.func,
  initialReplies: PropTypes.array,
  onReplyCreated: PropTypes.func
}

export default SocialReplies
